<template>
    <div>
        <h4 v-if="title" class="block text-[#6B7280] font-medium mb-2 text-xl">
            {{ title }}
        </h4>

        <p v-if="description" class="text-gray-500 text-xs mb-2">{{ description }}</p>

        <div class="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
            <!-- Slider -->
            <input
                type="range"
                :value="internalValue"
                :min="min"
                :max="max"
                :step="step"
                :disabled="disabled"
                class="flex-1 h-2 rounded-lg appearance-none cursor-pointer range-track"
                @input="onInput"
            />

            <!-- Number input -->
            <div class="flex-shrink-0 w-full sm:w-32">
                <div class="relative">
                    <span
                        v-if="prefix"
                        class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
                    >{{ prefix }}</span>
                    <input
                        type="number"
                        :value="internalValue"
                        :min="min"
                        :max="max"
                        :step="step"
                        readonly
                        :class="['w-full pr-3 py-1 border border-[#525252] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500', prefix ? 'pl-7' : 'pl-3']"
                        @input="onInput"
                        @blur="onBlur"
                    />
                </div>
            </div>
        </div>

        <span v-if="error" class="text-red-500 text-sm mt-1 block">{{ error }}</span>
    </div>
</template>

<script>
export default {
    name: 'Range',

    model: {
        prop: 'value',
        event: 'change',
    },

    props: {
        value: {
            type: Number,
            default: 0,
        },
        min: {
            type: Number,
            default: 0,
        },
        max: {
            type: Number,
            default: 100,
        },
        step: {
            type: Number,
            default: 1,
        },
        title: {
            type: String,
            default: '',
        },
        description: {
            type: String,
            default: '',
        },
        prefix: {
            type: String,
            default: '$',
        },
        error: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            internalValue: this.value,
        };
    },

    mounted() {
        const clamped = Math.min(this.max, Math.max(this.min, this.internalValue));
        if (clamped !== this.internalValue) {
            this.internalValue = clamped;
            this.$emit('change', clamped);
            this.$emit('input', clamped);
        }
    },

    watch: {
        value(val) {
            this.internalValue = val;
        },
    },

    methods: {
        onInput(e) {
            let val = Number(e.target.value);
            val = Math.min(this.max, Math.max(this.min, val));
            this.internalValue = val;
            this.$emit('change', val);
            this.$emit('input', val);
        },

        onBlur(e) {
            // Clamp on blur in case user typed out-of-range value
            let val = Number(e.target.value);
            val = Math.min(this.max, Math.max(this.min, val));
            if (val !== this.internalValue) {
                this.internalValue = val;
                this.$emit('change', val);
                this.$emit('input', val);
            }
        },
    },
};
</script>

<style scoped>
input[type="range"].range-track {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    height: 20px; /* enough room for the thumb without stretching the track */
}

input[type="range"].range-track::-webkit-slider-runnable-track {
    height: 8px;
    background-color: #d9d9d9;
    border-radius: 9999px;
}

input[type="range"].range-track::-moz-range-track {
    height: 8px;
    background-color: #d9d9d9;
    border-radius: 9999px;
}

input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #e69e4f;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    margin-top: -6px; /* vertically centre thumb on track */
}

input[type="range"]::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #e69e4f;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
</style>
